import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { BankAccountService } from '../../services/bankAccount.service';
import { BankReconciliationService } from '../../services/bankReconciliation.service';
import { PERMISSIONS } from '../../constants/permissions';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { BANK_REC_STATUS_COLORS, BankReconciliation } from '../../interfaces/bank/bankReconciliation.interface';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import CreateReconciliationDialog from './CreateReconciliationDialog';
import ConfirmDialog from '../common/ConfirmDialog';

/** Only a draft that has never been completed is discardable from the list: a reopened
 * reconciliation is IN_PROGRESS too, but it already owns a `STMT-` number and discarding it
 * would leave a gap in the sequence. */
const isDiscardableDraft = (row: BankReconciliation) => row.status === 'IN_PROGRESS' && !row.statementNumber;

/** Reconciliations for one bank account (T8, frontend part 1). `listByAccount` (and the bank
 * account lookup itself) 404 for an unknown or soft-deleted bank account -- rendered as an empty
 * state + toast rather than crashing the screen. */
const BankReconciliationList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_RECONCILIATION_MANAGE);
  const canEditAccount = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const { id } = useParams<{ id: string }>();

  const [bankAccount, setBankAccount] = useState<BankAccount | null>(null);
  const [reconciliations, setReconciliations] = useState<BankReconciliation[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [discardTarget, setDiscardTarget] = useState<BankReconciliation | null>(null);
  const [discarding, setDiscarding] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setNotFound(false);
      const [account, list] = await Promise.all([
        BankAccountService.getById(id),
        BankReconciliationService.listByAccount(id),
      ]);
      setBankAccount(account);
      setReconciliations(list);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setNotFound(true);
        setBankAccount(null);
        setReconciliations([]);
        toast.showApiError(e, 'Bank account not found');
      } else {
        toast.showApiError(e, 'Failed to load reconciliations');
      }
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleDiscard = async () => {
    if (!discardTarget) return;
    try {
      setDiscarding(true);
      await BankReconciliationService.remove(discardTarget.id);
      toast.showSuccess('Draft reconciliation discarded');
      setDiscardTarget(null);
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to discard draft reconciliation');
    } finally {
      setDiscarding(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  if (notFound || !bankAccount) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Reconciliations</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          This bank account could not be found. It may have been deleted.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/banking/accounts')}>Back to Bank Accounts</Button>
      </Box>
    );
  }

  return (
    <Box data-help-page="banking.recList.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5">{bankAccount.bankName} — {bankAccount.accountNumber}</Typography>
          <Typography variant="body2" color="text.secondary">
            {bankAccount.branchName ? `${bankAccount.branchName} · ` : ''}
            GL: {bankAccount.glCashAccountCode ? `${bankAccount.glCashAccountCode} - ${bankAccount.glCashAccountName}` : '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canEditAccount && (
            <Button data-help="banking.recList.editAccount" variant="outlined" onClick={() => navigate(`/banking/accounts/${bankAccount.id}/edit`)}>
              Edit account
            </Button>
          )}
          {canManage && (
            <Button data-help="banking.recList.new" variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              New Reconciliation
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer data-help-section="banking.recList.table" component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell data-help="banking.recList.col.number">Statement #</TableCell>
              <TableCell data-help="banking.recList.col.date">Statement Date</TableCell>
              <TableCell data-help="banking.recList.col.closing">Closing Balance</TableCell>
              <TableCell data-help="banking.recList.col.status">Status</TableCell>
              {canManage && <TableCell data-help="banking.recList.col.actions" align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {reconciliations.map(row => (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: 'pointer' }}
                data-testid={`bank-reconciliation-row-${row.id}`}
                onClick={() => navigate(`/banking/reconciliations/${row.id}`)}
              >
                <TableCell>{row.statementNumber || 'Draft'}</TableCell>
                <TableCell>{row.statementDate ? formatDate(row.statementDate) : '—'}</TableCell>
                <TableCell align="right">{formatMoney(row.statementClosingBalance)}</TableCell>
                <TableCell>
                  <Chip size="small" label={humanizeEnum(row.status)} color={BANK_REC_STATUS_COLORS[row.status] ?? 'default'} />
                </TableCell>
                {canManage && (
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {isDiscardableDraft(row) && (
                      <Tooltip title="Discard draft">
                        <IconButton data-help="banking.recList.action.discard"
                          size="small"
                          aria-label="discard draft"
                          data-testid={`bank-reconciliation-discard-${row.id}`}
                          onClick={() => setDiscardTarget(row)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {reconciliations.length === 0 && (
              <TableRow><TableCell colSpan={canManage ? 5 : 4} align="center">No reconciliations yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={discardTarget !== null}
        title="Discard draft reconciliation?"
        message={`The draft dated ${discardTarget?.statementDate ? formatDate(discardTarget.statementDate) : '—'} and its imported statement lines will be removed. Matches it holds are released. This cannot be undone.`}
        confirmLabel="Discard"
        confirmColor="error"
        submitting={discarding}
        onCancel={() => setDiscardTarget(null)}
        onConfirm={handleDiscard}
      />

      {dialogOpen && (
        <CreateReconciliationDialog
          bankAccountId={bankAccount.id}
          institutionId={bankAccount.institutionId}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default BankReconciliationList;
